/**
 * Link-equity funnel.
 *
 * Our strongest, most-linked pages ("equity sources") pass authority to weaker
 * URLs through contextual, in-content links. Rather than repeating one identical
 * block everywhere (which concentrates equity on the same handful of URLs), each
 * source page gets a deterministic rotation of the target pool, so every weak
 * URL receives inbound links from at least one strong page.
 *
 * `artifacts/link-check/link-report.md` lists pages with 0-1 internal inbound
 * links — that report is what feeds new entries into `EQUITY_TARGETS`.
 */

import { LEAGUE_CLUSTERS, MARKET_CLUSTERS, GUIDES } from "@/lib/seoClusters";
import { resolveInternalHref } from "@/lib/seo/internalHref";

/** Curated authority pages that funnel equity outward. */
export const EQUITY_SOURCES = [
  "/",
  "/predictions",
  "/ai-predictions",
  "/high-odds",
  "/blog",
  "/guides",
] as const;

export interface EquityTarget {
  path: string;
  label: string;
  /** Short contextual sentence fragment used as the surrounding prose. */
  context: string;
  group: "leagues" | "markets" | "guides" | "sports" | "data" | "entities";
}

const STATIC_TARGETS: EquityTarget[] = [
  {
    path: "/track-record",
    label: "verified prediction track record",
    context: "See how every published call has actually settled in our",
    group: "data",
  },
  {
    path: "/performance",
    label: "engine performance dashboard",
    context: "Model accuracy, edge and market breakdowns live in the",
    group: "data",
  },
  {
    path: "/historical-odds",
    label: "historical odds archive",
    context: "Compare closing prices against opening lines in the",
    group: "data",
  },
  {
    path: "/fixtures",
    label: "fixture archive",
    context: "Browse every analysed match in the",
    group: "data",
  },
  {
    path: "/safe-bets",
    label: "low-variance safe bets",
    context: "Prefer shorter prices? Start with our",
    group: "markets",
  },
  {
    path: "/jackpot-predictions",
    label: "jackpot predictions",
    context: "Multi-fixture coupons are covered in our",
    group: "markets",
  },
  {
    path: "/predictions/bonus-picks-today",
    label: "bonus picks for today",
    context: "Our highest-conviction extras appear in",
    group: "markets",
  },
  {
    path: "/nfl-predictions",
    label: "NFL predictions",
    context: "American football coverage lives in our",
    group: "sports",
  },
  {
    path: "/nba-predictions",
    label: "NBA predictions",
    context: "Basketball models are published as",
    group: "sports",
  },
  {
    path: "/mlb-predictions",
    label: "MLB predictions",
    context: "Baseball run-line analysis sits in our",
    group: "sports",
  },
  {
    path: "/nhl-predictions",
    label: "NHL predictions",
    context: "Ice-hockey puck-line calls are in our",
    group: "sports",
  },
  {
    path: "/clubs",
    label: "club intelligence hubs",
    context: "Team-by-team form and trends are indexed across our",
    group: "entities",
  },
  {
    path: "/players",
    label: "player intelligence hubs",
    context: "Individual contributions are tracked in our",
    group: "entities",
  },
  {
    path: "/competitions",
    label: "competition directory",
    context: "Every tournament we model is listed in the",
    group: "entities",
  },
  {
    path: "/countries",
    label: "country directory",
    context: "Browse by nation in our",
    group: "entities",
  },
  {
    path: "/leagues",
    label: "league prediction hubs",
    context: "Domestic-league analysis is grouped in our",
    group: "leagues",
  },
  {
    path: "/markets",
    label: "betting market hubs",
    context: "Market-by-market breakdowns start at our",
    group: "markets",
  },
];

/** Full weak-URL pool: static hubs plus every cluster and guide page. */
export const EQUITY_TARGETS: EquityTarget[] = [
  ...STATIC_TARGETS,
  ...LEAGUE_CLUSTERS.map<EquityTarget>((c) => ({
    path: `/leagues/${c.slug}`,
    label: c.label.toLowerCase(),
    context: "For league-specific analysis, read our",
    group: "leagues",
  })),
  ...MARKET_CLUSTERS.map<EquityTarget>((c) => ({
    path: `/markets/${c.slug}`,
    label: c.label.toLowerCase(),
    context: "For market-specific value, see our",
    group: "markets",
  })),
  ...GUIDES.map<EquityTarget>((g) => ({
    path: `/guides/${g.slug}`,
    label: g.label.toLowerCase(),
    context: "New to this? Start with",
    group: "guides",
  })),
];

/** Stable 32-bit hash so rotation is identical on server and client. */
function hash(input: string): number {
  let h = 2166136261;
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

/** Strip a locale prefix so `/fr/high-odds` matches the `/high-odds` source. */
export function sourceKey(path: string): string {
  return resolveInternalHref(path).replace(/^\/[a-z]{2,3}(?:-[a-z]{2})?(?=\/|$)/, "") || "/";
}

/** True when this route is one of the curated equity sources. */
export function isEquitySource(path: string): boolean {
  return (EQUITY_SOURCES as readonly string[]).includes(sourceKey(path));
}

/**
 * Deterministic slice of the target pool for a given page. Different source
 * pages start at different offsets, so equity spreads across the whole pool
 * instead of piling onto the first few entries.
 */
export function equityLinksFor(path: string, count = 8): EquityTarget[] {
  const key = sourceKey(path);
  const pool = EQUITY_TARGETS.filter((t) => t.path !== key);
  if (pool.length === 0) return [];
  const start = hash(key) % pool.length;
  const take = Math.min(count, pool.length);
  const out: EquityTarget[] = [];
  const seen = new Set<string>();
  for (let i = 0; out.length < take; i++) {
    const item = pool[(start + i * 7) % pool.length];
    if (seen.has(item!.path)) continue;
    seen.add(item!.path);
    out.push(item!);
    if (seen.size >= pool.length) break;
  }
  return out;
}
